import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '../../atoms/Icon'
import { useColumnDragResize } from './useColumnDragResize'
import { exportToExcel } from './exportUtils'
import {
  getStorageKeys,
  getStoredValue,
  storeValue,
  getOrderedVisibleColumns,
  getSortClass,
  getPaginationInfo,
  getPageNumbers,
  formatNumber,
} from './DataTable.utils'
import type { DataTableProps } from './DataTable.types'
import {
  DataTableContainer,
  DataTableHeader,
  DataTableActions,
  ColumnSettingsBtn,
  ColumnDropdown,
  ColumnDropdownHeader,
  ColumnDropdownHint,
  ColumnItem,
  ColumnCheckbox,
  ColumnLabel,
  TableWrapper,
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  CheckboxCell,
  CheckboxCellHeader,
  PaginationContainer,
  PaginationInfo,
  PaginationControls,
  PaginationBtn,
  PaginationPages,
  PaginationPage,
  PaginationPerPage,
  PerPageLabel,
  PerPageSelect,
  BulkActionsBar,
  BulkSelectionInfo,
  BulkActions,
  BulkActionBtn,
  EmptyState,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription,
  ResizeHandle,
  DragHandle,
  DragPreview,
  DetailPanel,
} from './DataTable.styles'

export const DataTable = <T extends Record<string, any>>({
  // Data
  data,
  columns,
  loading = false,
  error = null,
  emptyState,

  // Row actions
  onRowClick,
  onRowHover,
  rowActions,

  // Sorting
  sort = [],
  onSort,

  // Pagination
  pagination,

  // Selection
  selection,

  // Column management
  columnManagement,

  // Bulk actions
  bulkActions,

  // Export
  enableExport = false,
  exportConfig,
  exportColumns,
  onExcelExport,

  // Detail view
  detailView,

  // Styling
  className,
  stickyHeader = false,
  bordered = false,
  hover = true,
  compact = false,
}: DataTableProps<T>) => {
  const { t } = useTranslation('ui')
  const [columnDropdownOpen, setColumnDropdownOpen] = useState(false)
  const [selectedDetailItem, setSelectedDetailItem] = useState<T | null>(null)

  // Get storage keys if column management is enabled
  const storageKeys = columnManagement?.storageKey
    ? getStorageKeys(columnManagement.storageKey)
    : null

  // Initialize column state from storage or defaults
  const [localColumnVisibility, setLocalColumnVisibility] = useState(() => {
    if (columnManagement) return columnManagement.visibility
    if (storageKeys) {
      return getStoredValue(
        storageKeys.visibility,
        columns.reduce((acc, col) => ({ ...acc, [col.key]: true }), {})
      )
    }
    return columns.reduce((acc, col) => ({ ...acc, [col.key]: true }), {})
  })

  const [localColumnWidths, setLocalColumnWidths] = useState(() => {
    if (columnManagement) return columnManagement.widths
    if (storageKeys) return getStoredValue(storageKeys.widths, {})
    return {}
  })

  const [localColumnOrder, setLocalColumnOrder] = useState(() => {
    if (columnManagement) return columnManagement.order
    if (storageKeys) {
      return getStoredValue(
        storageKeys.order,
        columns.map((col) => col.key)
      )
    }
    return columns.map((col) => col.key)
  })

  // Get final column state (controlled or uncontrolled)
  const columnVisibility = columnManagement?.visibility ?? localColumnVisibility
  const columnWidths = columnManagement?.widths ?? localColumnWidths
  const columnOrder = columnManagement?.order ?? localColumnOrder

  // Get visible columns in order
  const visibleColumns = getOrderedVisibleColumns(
    columns,
    columnOrder,
    columnVisibility,
    columnWidths
  )

  // Drag and resize handling
  const { dragState, dragPreviewRef, handleDragStart, handleResizeStart } =
    useColumnDragResize(
      columnManagement?.onReorder ??
        ((from, to) => {
          setLocalColumnOrder((prev) => {
            const newOrder = [...prev]
            const [removed] = newOrder.splice(from, 1)
            newOrder.splice(to, 0, removed)
            return newOrder
          })
        }),
      columnManagement?.onResize ??
        ((column, width) => {
          setLocalColumnWidths((prev) => ({ ...prev, [column]: width }))
        })
    )

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (
        !target.closest('[data-column-dropdown]') &&
        !target.closest('[data-column-settings-btn]')
      ) {
        setColumnDropdownOpen(false)
      }
    }

    if (columnDropdownOpen) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [columnDropdownOpen])

  // Save to localStorage when local state changes
  useEffect(() => {
    if (storageKeys && !columnManagement) {
      storeValue(storageKeys.visibility, localColumnVisibility)
    }
  }, [localColumnVisibility, storageKeys, columnManagement])

  useEffect(() => {
    if (storageKeys && !columnManagement) {
      storeValue(storageKeys.widths, localColumnWidths)
    }
  }, [localColumnWidths, storageKeys, columnManagement])

  useEffect(() => {
    if (storageKeys && !columnManagement) {
      storeValue(storageKeys.order, localColumnOrder)
    }
  }, [localColumnOrder, storageKeys, columnManagement])

  // Handle column visibility change
  const handleColumnVisibilityChange = useCallback(
    (column: string, visible: boolean) => {
      if (columnManagement?.onVisibilityChange) {
        columnManagement.onVisibilityChange(column, visible)
      } else {
        setLocalColumnVisibility((prev) => ({ ...prev, [column]: visible }))
      }
    },
    [columnManagement]
  )

  // Handle reset columns
  const handleResetColumns = useCallback(() => {
    if (columnManagement?.onReset) {
      columnManagement.onReset()
    } else {
      const defaultVisibility = columns.reduce(
        (acc, col) => ({ ...acc, [col.key]: true }),
        {}
      )
      const defaultOrder = columns.map((col) => col.key)

      setLocalColumnVisibility(defaultVisibility)
      setLocalColumnWidths({})
      setLocalColumnOrder(defaultOrder)

      if (storageKeys) {
        window.localStorage.removeItem(storageKeys.visibility)
        window.localStorage.removeItem(storageKeys.widths)
        window.localStorage.removeItem(storageKeys.order)
      }
    }
  }, [columns, columnManagement, storageKeys])

  // Handle excel export
  const handleExcelExport = useCallback(() => {
    if (!selection) return

    const selectedItems = data.filter((item) =>
      selection.selectedRows.has(selection.getRowId(item))
    )

    if (selectedItems.length === 0) return

    // If parent component provides custom export handler, use it
    if (onExcelExport) {
      onExcelExport(selectedItems)
    } else if (exportColumns) {
      // Use exportColumns for export - convert to DataTableColumn format
      const columnsToUse = exportColumns.map((col) => ({
        key: col.key,
        label: col.label,
        exportFormatter: col.getValue,
      }))

      exportToExcel({
        data: selectedItems,
        columns: columnsToUse,
        filename: exportConfig?.filename,
      })
    } else {
      // Default implementation: export using display columns
      exportToExcel({
        data: selectedItems,
        columns: columns,
        filename: exportConfig?.filename,
        getExportData: exportConfig?.getExportData,
      })
    }

    // Clear selection after export
    selection.onSelectAll(false)
  }, [data, selection, columns, exportColumns, exportConfig, onExcelExport])

  // Handle row click
  const handleRowClick = useCallback(
    (item: T) => {
      if (detailView) {
        setSelectedDetailItem(item)
      } else if (onRowClick) {
        onRowClick(item)
      }
    },
    [detailView, onRowClick]
  )

  // Calculate pagination info
  const paginationInfo = pagination
    ? getPaginationInfo(
        pagination.currentPage,
        pagination.pageSize,
        pagination.total
      )
    : null

  // Render empty state
  if (!loading && data.length === 0) {
    const emptyContent = emptyState || (
      <EmptyState>
        <EmptyIcon>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            width="48"
            height="48"
          >
            <path
              d="M12 2v20M2 12h20"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle cx="12" cy="12" r="9" strokeDasharray="4 2" />
          </svg>
        </EmptyIcon>
        <EmptyTitle>{t('dataTable.empty.title')}</EmptyTitle>
        <EmptyDescription>{t('dataTable.empty.description')}</EmptyDescription>
      </EmptyState>
    )

    return (
      <DataTableContainer className={className}>
        {emptyContent}
      </DataTableContainer>
    )
  }

  // Render error state
  if (error) {
    const errorContent = (
      <EmptyState>
        <EmptyIcon>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            width="48"
            height="48"
          >
            <circle cx="12" cy="12" r="10" />
            <path
              d="M12 8v4M12 16h.01"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </EmptyIcon>
        <EmptyTitle>{t('dataTable.error.title')}</EmptyTitle>
        <EmptyDescription>
          {typeof error === 'string' ? error : t('dataTable.error.description')}
        </EmptyDescription>
      </EmptyState>
    )

    return (
      <DataTableContainer className={className}>
        {errorContent}
      </DataTableContainer>
    )
  }

  return (
    <DataTableContainer className={className}>
      {/* Header with column settings */}
      <DataTableHeader>
        <DataTableActions>
          <div style={{ position: 'relative' }}>
            <ColumnSettingsBtn
              data-column-settings-btn
              onClick={(e) => {
                e.stopPropagation()
                setColumnDropdownOpen(!columnDropdownOpen)
              }}
            >
              <Icon name="settings" size="small" />
              {t('dataTable.columns.button')}
            </ColumnSettingsBtn>

            <ColumnDropdown $active={columnDropdownOpen} data-column-dropdown>
              <ColumnDropdownHeader>
                {t('dataTable.columns.showHide')}
              </ColumnDropdownHeader>
              <ColumnDropdownHint>
                {t('dataTable.columns.dragHint')}
              </ColumnDropdownHint>
              {columns.map((column) => (
                <ColumnItem key={column.key}>
                  <ColumnCheckbox
                    type="checkbox"
                    checked={
                      columnVisibility[
                        column.key as keyof typeof columnVisibility
                      ] ?? true
                    }
                    onChange={(e) =>
                      handleColumnVisibilityChange(column.key, e.target.checked)
                    }
                  />
                  <ColumnLabel>{column.label}</ColumnLabel>
                </ColumnItem>
              ))}
              <div
                style={{
                  marginTop: '12px',
                  paddingTop: '12px',
                  borderTop: '1px solid #e5e5e5',
                }}
              >
                <button
                  onClick={handleResetColumns}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    background: '#f5f5f5',
                    border: '1px solid #e5e5e5',
                    borderRadius: '4px',
                    fontSize: '12px',
                    color: '#666',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#e8e8e8'
                    e.currentTarget.style.color = '#333'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#f5f5f5'
                    e.currentTarget.style.color = '#666'
                  }}
                >
                  {t('dataTable.columns.resetToDefault')}
                </button>
              </div>
            </ColumnDropdown>
          </div>
        </DataTableActions>
      </DataTableHeader>

      {/* Table */}
      <TableWrapper $stickyHeader={stickyHeader}>
        <Table $bordered={bordered} $hover={hover} $compact={compact}>
          <TableHead>
            <tr>
              {selection && (
                <TableHeader style={{ width: '48px' }}>
                  <CheckboxCellHeader>
                    <input
                      type="checkbox"
                      checked={
                        selection.selectedRows.size === data.length &&
                        data.length > 0
                      }
                      onChange={(e) => selection.onSelectAll(e.target.checked)}
                    />
                  </CheckboxCellHeader>
                </TableHeader>
              )}
              {visibleColumns.map((column, index) => (
                <TableHeader
                  key={column.key}
                  className={
                    column.sortable && onSort
                      ? `sortable ${getSortClass(column.key, sort)}`
                      : ''
                  }
                  onClick={
                    column.sortable && onSort
                      ? () => onSort(column.key)
                      : undefined
                  }
                  style={{
                    width: column.width,
                    minWidth: column.minWidth,
                    position: 'relative',
                    opacity: dragState.dragIndex === index ? 0.5 : 1,
                  }}
                  $hideOnMobile={column.hideOnMobile}
                >
                  {columnManagement && (
                    <DragHandle
                      onMouseDown={(e) => handleDragStart(index, e)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                  {column.label}
                  {columnManagement && (
                    <ResizeHandle
                      onMouseDown={(e) => handleResizeStart(column.key, e)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                </TableHeader>
              ))}
              {rowActions && (
                <TableHeader style={{ width: '100px' }}>
                  {t('dataTable.actions')}
                </TableHeader>
              )}
            </tr>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={
                    visibleColumns.length +
                    (selection ? 1 : 0) +
                    (rowActions ? 1 : 0)
                  }
                  style={{ textAlign: 'center', padding: '48px' }}
                >
                  {t('dataTable.loading')}
                </TableCell>
              </TableRow>
            ) : (
              data.map((item) => {
                const rowId = selection?.getRowId(item) ?? item.id
                const isSelected = selection?.selectedRows.has(rowId) ?? false

                return (
                  <TableRow
                    key={rowId}
                    $selected={isSelected}
                    onClick={() => handleRowClick(item)}
                    onMouseEnter={() => onRowHover?.(item)}
                    onMouseLeave={() => onRowHover?.(null)}
                  >
                    {selection && (
                      <CheckboxCell>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            e.stopPropagation()
                            selection.onRowSelect(rowId, e.target.checked)
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </CheckboxCell>
                    )}

                    {visibleColumns.map((column) => (
                      <TableCell
                        key={column.key}
                        style={{ width: column.width }}
                        $hideOnMobile={column.hideOnMobile}
                      >
                        {column.render
                          ? column.render(item, column)
                          : (item[column.key] ?? '—')}
                      </TableCell>
                    ))}

                    {rowActions && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {rowActions(item)}
                      </TableCell>
                    )}
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </TableWrapper>

      {/* Pagination */}
      {pagination && paginationInfo && (
        <PaginationContainer>
          <PaginationInfo>
            {t('dataTable.pagination.showing')}{' '}
            <strong>
              {paginationInfo.startItem}-{paginationInfo.endItem}
            </strong>{' '}
            {t('dataTable.pagination.of')}{' '}
            <strong>
              {pagination.formatTotal
                ? pagination.formatTotal(pagination.total)
                : formatNumber(pagination.total)}
            </strong>{' '}
            {t('dataTable.pagination.items')}
          </PaginationInfo>

          <PaginationControls>
            <PaginationBtn
              disabled={pagination.currentPage === 1}
              onClick={() =>
                pagination.onPageChange(pagination.currentPage - 1)
              }
            >
              <Icon name="arrow-left" size="small" />
            </PaginationBtn>

            <PaginationPages>
              {getPageNumbers(
                pagination.currentPage,
                paginationInfo.totalPages
              ).map((pageNum) => (
                <PaginationPage
                  key={pageNum}
                  $active={pageNum === pagination.currentPage}
                  onClick={() => pagination.onPageChange(pageNum)}
                >
                  {pageNum}
                </PaginationPage>
              ))}
            </PaginationPages>

            <PaginationBtn
              disabled={pagination.currentPage === paginationInfo.totalPages}
              onClick={() =>
                pagination.onPageChange(pagination.currentPage + 1)
              }
            >
              <Icon name="arrow-right" size="small" />
            </PaginationBtn>
          </PaginationControls>

          <PaginationPerPage>
            <PerPageLabel>{t('dataTable.pagination.show')}:</PerPageLabel>
            <PerPageSelect
              value={pagination.pageSize}
              onChange={(e) =>
                pagination.onPageSizeChange(parseInt(e.target.value))
              }
            >
              {(pagination.pageSizeOptions || [10, 20, 50, 100]).map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </PerPageSelect>
          </PaginationPerPage>
        </PaginationContainer>
      )}

      {/* Bulk Actions Bar */}
      {selection && bulkActions && selection.selectedRows.size > 0 && (
        <BulkActionsBar>
          <BulkSelectionInfo>
            {t('dataTable.bulkActions.itemsSelected', {
              count: selection.selectedRows.size,
            })}
          </BulkSelectionInfo>
          <BulkActions>
            {enableExport && (
              <BulkActionBtn onClick={handleExcelExport} $variant="primary">
                <Icon name="download" size="small" />
                {t('dataTable.bulkActions.exportAsExcel')}
              </BulkActionBtn>
            )}
            {bulkActions.map((action, index) => (
              <BulkActionBtn
                key={index}
                onClick={() => {
                  const selectedItems = data.filter((item) =>
                    selection.selectedRows.has(selection.getRowId(item))
                  )
                  action.onClick(selectedItems)
                }}
                $variant={action.variant}
              >
                {action.icon}
                {action.label}
              </BulkActionBtn>
            ))}
          </BulkActions>
        </BulkActionsBar>
      )}

      {/* Drag preview */}
      {dragState.isDragging && dragState.dragIndex !== null && (
        <DragPreview ref={dragPreviewRef as any}>
          {visibleColumns[dragState.dragIndex]?.label}
        </DragPreview>
      )}

      {/* Detail Panel */}
      {detailView && (
        <DetailPanel
          $isOpen={selectedDetailItem !== null}
          $width={detailView.width}
        >
          {selectedDetailItem &&
            detailView.render(selectedDetailItem, () =>
              setSelectedDetailItem(null)
            )}
        </DetailPanel>
      )}
    </DataTableContainer>
  )
}
